/* eslint-disable complexity */
import { ActivitySectorHttp } from '@data/models/activity-sector.interface';
import { CityHttp } from '@data/models/city.interface';
import { DeviceTypeHttp } from '@data/models/device-type.interface';
import { QualificationHttp } from '@data/models/qualification.interface';
import { TrainingCourseCandidateHttp } from '@data/models/training-course-candidate.interface';
import {
  TrainingCourseContractHttp,
  TrainingCourseContractIncluded,
  TrainingCourseContractRawIncluded,
  TrainingCourseContractStatus,
  TrainingCourseContractWizard,
} from '@data/models/training-course-contract.interface';
import { TrainingCourseContract } from '@data/models/training-course-contract.interface';
import { TrainingCourseEducationalPathHttp } from '@data/models/training-course-educational-path.interface';
import { TrainingCourseEstimatorHttp } from '@data/models/training-course-estimator.interface';
import { TrainingCourseGroupHttp } from '@data/models/training-course-group.interface';
import { TrainingCourseSessionHttp } from '@data/models/training-course-session.interface';
import { TrainingCourseTrainingHttp } from '@data/models/training-course-training.interface';
import { TrainingCourseHttp } from '@data/models/training-course.interface';
import dayjs from 'dayjs';

import { toActivitySector } from './activity-sector.dto';
import { toQualification } from './qualification.dto';
import { fromTrainingCourseCandidateHttp } from './training-course-candidate.dto';
import { toEstimator } from './training-course-estimator.dto';
import { fromTrainingCourseGroupHttp } from './training-course-group.dto';

export const toTrainingCourseContractIncluded = (
  included: TrainingCourseContractRawIncluded = [],
): TrainingCourseContractIncluded => {
  return {
    groups: included.filter(
      ({ type }) => type === 'training_course_groups',
    ) as TrainingCourseGroupHttp[],
    candidates: included.filter(
      ({ type }) => type === 'training_course_candidates',
    ) as TrainingCourseCandidateHttp[],
    qualifications: included.filter(
      ({ type }) => type === 'qualification_repositories',
    ) as QualificationHttp[],
    sector: included.filter(
      ({ type }) => type === 'activity_sector_repositories',
    ) as ActivitySectorHttp[],
    training: included.filter(
      ({ type }) => type === 'training_course_trainings',
    ) as TrainingCourseTrainingHttp[],
    training_course: included.filter(
      ({ type }) => type === 'training_courses',
    ) as TrainingCourseHttp[],
    sessions: included.filter(
      ({ type }) => type === 'training_course_sessions',
    ) as TrainingCourseSessionHttp[],
    cities: included.filter(({ type }) => type === 'cities') as CityHttp[],
    estimators: included.filter(
      ({ type }) => type === 'training_course_estimators',
    ) as TrainingCourseEstimatorHttp[],
    educational_paths: included.filter(
      ({ type }) => type === 'training_course_educational_paths',
    ) as TrainingCourseEducationalPathHttp[],
    device_types: included.filter(
      ({ type }) => type === 'device_type_repositories',
    ) as DeviceTypeHttp[],
  };
};

export const toTrainingCourseContract = (
  trainingCourseContract: Partial<TrainingCourseContractHttp>,
  included: Partial<TrainingCourseContractIncluded>,
): TrainingCourseContract => {
  const groupHttp = included.groups?.find(
    (g) =>
      g.id ===
      trainingCourseContract.relationships?.training_course_group?.data?.id,
  );
  const candidatesHttp = included.candidates?.filter((c) =>
    trainingCourseContract.relationships?.candidates?.data
      ?.map((c) => c.id)
      .includes(c.id),
  );

  const qualificationHttp = included.qualifications?.find(
    ({ id }) =>
      id === trainingCourseContract.relationships?.qualification?.data?.id,
  );

  const activitySectorHttp = included.sector?.find(
    ({ id }) => id === trainingCourseContract.relationships?.sector?.data?.id,
  );

  const estimatorHttp = included.estimators?.find(
    ({ id }) =>
      id ===
      trainingCourseContract.relationships?.training_course_estimator?.data?.id,
  );

  return {
    id: trainingCourseContract.id ?? null,
    deviceTypeInitials:
      trainingCourseContract.attributes?.device_type_initials ?? '',
    status:
      trainingCourseContract.attributes?.status === 'draft'
        ? TrainingCourseContractStatus.DRAFT
        : TrainingCourseContractStatus.SENT_TO_LUCIE,
    trainingCourseId:
      trainingCourseContract.relationships?.training_course?.data?.id || null,
    companyAgencyClientAtlasId:
      trainingCourseContract.attributes?.company_agency_client_atlas_id ?? '',
    address: trainingCourseContract.attributes?.address ?? '',
    place: trainingCourseContract.attributes?.place ?? '',
    city: trainingCourseContract.attributes?.city ?? '',
    zipCode: trainingCourseContract.attributes?.zip_code ?? '',
    clientId: trainingCourseContract.attributes?.client_id ?? '',
    clientName: trainingCourseContract.attributes?.client_name ?? '',
    clientSiret: trainingCourseContract.attributes?.client_siret ?? '',
    clientCode: trainingCourseContract.attributes?.client_code ?? '',
    clientRegistrationNumber:
      trainingCourseContract.attributes?.client_registration_number ?? '',
    siteCompanyName: trainingCourseContract.attributes?.site_company_name ?? '',
    siteCode: trainingCourseContract.attributes?.site_code ?? '',
    siteCity: trainingCourseContract.attributes?.site_city ?? '',
    sitePhone: trainingCourseContract.attributes?.site_phone ?? '',
    siteZipCode: trainingCourseContract.attributes?.site_zip_code ?? '',
    siteAddress: trainingCourseContract.attributes?.site_address ?? '',
    startDate: trainingCourseContract.attributes?.start_date
      ? dayjs(trainingCourseContract.attributes.start_date)
      : null,
    endDate: trainingCourseContract.attributes?.end_date
      ? dayjs(trainingCourseContract.attributes.end_date)
      : null,
    trainingCourseGroup: fromTrainingCourseGroupHttp(groupHttp ?? {}),
    candidates:
      candidatesHttp?.map((c) => fromTrainingCourseCandidateHttp(c, [], [])) ||
      [],
    qualification: toQualification(qualificationHttp || {}),
    particularConditions:
      trainingCourseContract.attributes?.particular_conditions ?? '',
    justification: trainingCourseContract.attributes?.justification ?? '',
    sector: toActivitySector(activitySectorHttp ?? {}),
    trainingCourseEstimator: toEstimator(estimatorHttp || {}, included),
    nonWorkingPeriod:
      trainingCourseContract.attributes?.non_working_period ?? '',
    trialPeriod: trainingCourseContract.attributes?.trial_period ?? undefined,
  };
};

export const toTrainingCourseContractWizard = (
  trainingCourseContract: Partial<TrainingCourseContract>,
): TrainingCourseContractWizard => {
  return {
    type: 'training_course_contracts',
    id: trainingCourseContract.id ?? '',
    attributes: {
      company_agency_client_atlas_id:
        trainingCourseContract.companyAgencyClientAtlasId ?? '',
      device_type_initials: trainingCourseContract.deviceTypeInitials ?? '',
      address: trainingCourseContract.address ?? '',
      place: trainingCourseContract.place ?? '',
      city: trainingCourseContract.city ?? '',
      zip_code: trainingCourseContract.zipCode ?? '',
      client_id: trainingCourseContract.clientId ?? '',
      client_name: trainingCourseContract.clientName ?? '',
      client_siret: trainingCourseContract.clientSiret ?? '',
      client_code: trainingCourseContract.clientCode ?? '',
      client_registration_number:
        trainingCourseContract.clientRegistrationNumber ?? '',
      site_company_name: trainingCourseContract.siteCompanyName ?? '',
      site_code: trainingCourseContract.siteCode ?? '',
      site_city: trainingCourseContract.siteCity ?? '',
      site_phone: trainingCourseContract.sitePhone ?? '',
      site_zip_code: trainingCourseContract.siteZipCode ?? '',
      site_address: trainingCourseContract.siteAddress ?? '',
      start_date: trainingCourseContract.startDate ?? null,
      end_date: trainingCourseContract.endDate ?? null,
      particular_conditions: trainingCourseContract.particularConditions ?? '',
      justification: trainingCourseContract.justification ?? '',
      status:
        trainingCourseContract.status ?? TrainingCourseContractStatus.DRAFT,
      non_working_period: trainingCourseContract.nonWorkingPeriod ?? '',
      trial_period: trainingCourseContract.trialPeriod ?? undefined,
    },
    relationships: {
      training_course: {
        data: {
          type: 'training_courses',
          id: trainingCourseContract.trainingCourseId ?? '',
        },
      },
      training_course_group: {
        data: {
          type: 'training_course_groups',
          id: trainingCourseContract.trainingCourseGroup?.id ?? '',
        },
      },
      candidates: {
        data:
          trainingCourseContract.candidates?.map((c) => ({
            type: 'training_course_candidates',
            id: c.id,
          })) ?? [],
      },
      ...(trainingCourseContract.qualification?.id
        ? {
            qualification: {
              data: {
                type: 'qualification_repositories',
                id: trainingCourseContract.qualification.id,
              },
            },
          }
        : {}),
      ...(trainingCourseContract.trainingCourseEstimator?.id
        ? {
            training_course_estimator: {
              data: {
                type: 'training_course_estimators',
                id: trainingCourseContract.trainingCourseEstimator.id,
              },
            },
          }
        : {}),
      ...(trainingCourseContract.sector?.id
        ? {
            sector: {
              data: {
                type: 'activity_sector_repositories',
                id: trainingCourseContract.sector.id,
              },
            },
          }
        : {}),
    },
  };
};
